import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin, Users, Wifi, Waves, Car, Music, UtensilsCrossed,
  ChevronLeft, ChevronRight, Phone, CheckCircle, Star, MessageCircle
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import StarRating, { RatingBadge } from '../components/StarRating';
import ReviewCard from '../components/ReviewCard';
import BookingWidget from '../components/BookingWidget';
import { propertiesAPI, enquiriesAPI, reviewsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const AMENITY_MAP = {
  pool: { icon: <Waves size={16} />, label: 'Swimming Pool' },
  wifi: { icon: <Wifi size={16} />, label: 'WiFi' },
  parking: { icon: <Car size={16} />, label: 'Parking' },
  music: { icon: <Music size={16} />, label: 'Music System' },
  ac: { icon: <div className="text-xs font-bold">AC</div>, label: 'Air Conditioning' },
  bbq: { icon: <UtensilsCrossed size={16} />, label: 'BBQ' },
  kitchen: { icon: <UtensilsCrossed size={16} />, label: 'Kitchen' },
  gym: { icon: <div className="text-xs font-bold">GYM</div>, label: 'Gym' },
  garden: { icon: <div className="text-xs font-semibold">GDN</div>, label: 'Garden' },
  restaurant: { icon: <UtensilsCrossed size={16} />, label: 'Restaurant' },
  catering: { icon: <UtensilsCrossed size={16} />, label: 'Catering' },
  overnight: { icon: <div className="text-xs font-bold">24H</div>, label: 'Overnight Stay' },
};

const TYPE_LABELS = {
  homestay: 'Homestay', hotel: 'Hotel', pg: 'PG', hostel: 'Hostel',
  farmhouse: 'Farmhouse', penthouse: 'Penthouse',
  function_hall: 'Function Hall', banquet_hall: 'Banquet Hall'
};

// ---- Rating Distribution Bar ----
const RatingBar = ({ star, count, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-gray-500 w-2 shrink-0">{star}</span>
      <Star size={11} className="text-gold fill-gold shrink-0" fill="#C9A84C" strokeWidth={0} />
      <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="h-full bg-gold rounded-full"
        />
      </div>
      <span className="text-gray-400 w-4 text-right">{count}</span>
    </div>
  );
};

// ---- Review Form ----
const ReviewForm = ({ propertyId, propertyName, user, onSuccess }) => {
  const [form, setForm] = useState({
    rating: 0, title: '', comment: '',
    customer_name: user?.name || '',
    customer_email: user?.email || ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.rating === 0) { setError('Please select a star rating'); return; }
    setSubmitting(true); setError('');
    try {
      await reviewsAPI.create({ ...form, property_id: propertyId });
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to submit review');
    } finally { setSubmitting(false); }
  };

  return (
    <form data-testid="review-form" onSubmit={handleSubmit} className="space-y-4">
      {/* Star selector */}
      <div>
        <label className="text-xs text-gray-500 font-medium block mb-2">Your Rating *</label>
        <div className="flex items-center gap-3">
          <StarRating value={form.rating} onChange={(r) => setForm(p => ({ ...p, rating: r }))} size="lg" />
          {form.rating > 0 && (
            <span className="text-sm font-medium text-navy">
              {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][form.rating]}
            </span>
          )}
        </div>
      </div>

      {!user && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1.5">Your Name *</label>
            <input required type="text" value={form.customer_name} placeholder="Full name"
              onChange={e => setForm(p => ({ ...p, customer_name: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-navy outline-none focus:border-gold" />
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1.5">Email *</label>
            <input required type="email" value={form.customer_email} placeholder="your@email.com"
              onChange={e => setForm(p => ({ ...p, customer_email: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-navy outline-none focus:border-gold" />
          </div>
        </div>
      )}

      <div>
        <label className="text-xs text-gray-500 font-medium block mb-1.5">Headline (optional)</label>
        <input type="text" value={form.title} placeholder="Summarize your experience in one line"
          onChange={e => setForm(p => ({ ...p, title: e.target.value }))} maxLength={80}
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-navy outline-none focus:border-gold" />
      </div>

      <div>
        <label className="text-xs text-gray-500 font-medium block mb-1.5">Your Review *</label>
        <textarea required rows={4} value={form.comment} placeholder="What did you love? What could be better?"
          onChange={e => setForm(p => ({ ...p, comment: e.target.value }))} minLength={10}
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-navy outline-none focus:border-gold resize-none" />
      </div>

      {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

      <button data-testid="submit-review" type="submit" disabled={submitting}
        className="w-full bg-navy hover:bg-navy-light text-white py-3.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        {submitting
          ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting...</>
          : <><Star size={15} />Submit Review</>
        }
      </button>
      <p className="text-xs text-gray-400 text-center">Reviews are moderated before publishing.</p>
    </form>
  );
};

// ---- Reviews Section ----
const ReviewsSection = ({ propertyId, propertyName }) => {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], avg_rating: 0, total: 0, distribution: {} });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const load = () => {
    reviewsAPI.getByProperty(propertyId)
      .then(setData).catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [propertyId]);

  return (
    <div data-testid="reviews-section" className="mt-12 pt-10 border-t border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-semibold text-navy text-xl mb-1">Guest Reviews</h2>
          {data.total > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} size={16}
                    className={data.avg_rating >= s ? 'text-gold' : 'text-gray-200'}
                    fill={data.avg_rating >= s ? '#C9A84C' : 'none'}
                    strokeWidth={data.avg_rating >= s ? 0 : 1.5}
                  />
                ))}
              </div>
              <span className="font-bold text-navy">{data.avg_rating.toFixed(1)}</span>
              <span className="text-gray-400 text-sm">· {data.total} review{data.total !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
        {!submitted && (
          <button
            data-testid="write-review-btn"
            onClick={() => setShowForm(s => !s)}
            className="flex items-center gap-2 text-sm font-medium border border-navy text-navy hover:bg-navy hover:text-white px-4 py-2 rounded-xl transition-colors"
          >
            <Star size={14} />
            {showForm ? 'Cancel' : 'Write a Review'}
          </button>
        )}
      </div>

      {/* Rating distribution */}
      {data.total > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-8">
          <div className="bg-[#FAFAF9] rounded-2xl p-6 text-center">
            <div className="font-serif text-6xl font-black text-navy mb-1">{data.avg_rating.toFixed(1)}</div>
            <div className="flex justify-center mb-2">
              {[1,2,3,4,5].map(s => (
                <Star key={s} size={18}
                  className={data.avg_rating >= s ? 'text-gold' : 'text-gray-200'}
                  fill={data.avg_rating >= s ? '#C9A84C' : 'none'}
                  strokeWidth={data.avg_rating >= s ? 0 : 1.5}
                />
              ))}
            </div>
            <p className="text-gray-400 text-sm">{data.total} review{data.total !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex flex-col justify-center gap-2">
            {[5,4,3,2,1].map(star => (
              <RatingBar key={star} star={star} count={data.distribution?.[String(star)] || 0} total={data.total} />
            ))}
          </div>
        </div>
      )}

      {/* Write review form */}
      <AnimatePresence>
        {showForm && !submitted && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8 bg-[#FAFAF9] rounded-2xl p-6 border border-gray-100 overflow-hidden"
          >
            <h3 className="font-semibold text-navy mb-4">Share your experience</h3>
            <ReviewForm
              propertyId={propertyId}
              propertyName={propertyName}
              user={user}
              onSuccess={() => { setSubmitted(true); setShowForm(false); }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {submitted && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="mb-8 bg-green-50 border border-green-200 rounded-2xl p-6 flex items-center gap-4">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center shrink-0">
            <CheckCircle size={24} className="text-green-500" />
          </div>
          <div>
            <p className="font-semibold text-green-800">Review submitted!</p>
            <p className="text-green-600 text-sm">Your review is under review and will be published shortly.</p>
          </div>
        </motion.div>
      )}

      {/* Reviews list */}
      {loading ? (
        <div className="space-y-4">
          {[1,2].map(i => <div key={i} className="h-28 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      ) : data.total === 0 ? (
        <div className="text-center py-12">
          <MessageCircle size={36} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 text-sm font-medium">No reviews yet</p>
          <p className="text-gray-400 text-xs mt-1">Be the first to share your experience!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {data.reviews.map((review, i) => <ReviewCard key={review.id} review={review} index={i} />)}
        </div>
      )}
    </div>
  );
};

// ---- Main Component ----
const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [rightPanel, setRightPanel] = useState('book'); // 'book' | 'enquiry'
  const [reviewSummary, setReviewSummary] = useState({ avg_rating: 0, total: 0 });
  const [enquiryForm, setEnquiryForm] = useState({
    name: '', email: '', phone: '', guests: 2, check_in: '', check_out: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    propertiesAPI.getById(id).then(setProperty).catch(() => navigate('/search')).finally(() => setLoading(false));
    reviewsAPI.getByProperty(id).then(d => setReviewSummary({ avg_rating: d.avg_rating, total: d.total })).catch(() => {});
  }, [id]);

  const handleEnquiry = async (e) => {
    e.preventDefault(); setSubmitting(true); setError('');
    try {
      await enquiriesAPI.create({ ...enquiryForm, property_id: id });
      setSubmitted(true);
    } catch (err) { setError(err.message || 'Failed to send enquiry.'); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!property) return null;

  const images = property.images?.length > 0 ? property.images : ['https://images.unsplash.com/photo-1607567618395-62fc2d132c3e?w=1200'];

  return (
    <div data-testid="property-detail-page" className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Gallery */}
      <div className="pt-16">
        <div className="relative h-[60vh] min-h-[400px] bg-navy-dark overflow-hidden">
          <img src={images[activeImg]} alt={property.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/60 to-transparent" />
          {images.length > 1 && (
            <>
              <button onClick={() => setActiveImg(i => (i - 1 + images.length) % images.length)}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white p-3 rounded-full">
                <ChevronLeft size={20} />
              </button>
              <button onClick={() => setActiveImg(i => (i + 1) % images.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white p-3 rounded-full">
                <ChevronRight size={20} />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {images.map((_, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    className={`h-2 rounded-full transition-all ${i === activeImg ? 'bg-white w-6' : 'bg-white/50 w-2'}`} />
                ))}
              </div>
            </>
          )}
          <span className="absolute bottom-4 right-4 bg-white text-navy text-xs font-semibold px-4 py-2 rounded-full">
            {images.length} photo{images.length !== 1 ? 's' : ''}
          </span>
          <button onClick={() => navigate(-1)}
            className="absolute top-6 left-6 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white p-2.5 rounded-full">
            <ChevronLeft size={18} />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left */}
          <div className="lg:col-span-2">
            {/* Header */}
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="bg-gold/10 text-gold text-xs font-semibold px-3 py-1 rounded-full">
                  {TYPE_LABELS[property.type] || property.type}
                </span>
                {property.experiences?.map(exp => (
                  <span key={exp} className="bg-navy/10 text-navy text-xs font-medium px-3 py-1 rounded-full capitalize">{exp}</span>
                ))}
              </div>
              <h1 data-testid="property-name" className="font-serif text-3xl lg:text-4xl font-black text-navy tracking-tight mb-3">
                {property.name}
              </h1>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5 text-gray-500 text-sm">
                  <MapPin size={14} className="text-gold" />
                  <span>{[property.location?.area, property.location?.city, property.location?.state].filter(Boolean).join(', ')}</span>
                </div>
                {reviewSummary.total > 0 && (
                  <RatingBadge rating={reviewSummary.avg_rating} count={reviewSummary.total} size="md" />
                )}
              </div>
            </div>

            {/* Price & capacity */}
            <div className="flex items-center gap-6 py-6 border-y border-gray-100 mb-8">
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Starting from</div>
                <div className="font-bold text-2xl text-gold">₹{property.price_min?.toLocaleString('en-IN')}</div>
              </div>
              <div className="w-px h-10 bg-gray-100" />
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Max capacity</div>
                <div className="flex items-center gap-1.5 font-semibold text-navy">
                  <Users size={16} className="text-gold" />{property.max_guests} guests
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-10">
              <h2 className="font-semibold text-navy text-lg mb-3">About this property</h2>
              <p className="text-gray-500 leading-relaxed text-sm">{property.description}</p>
            </div>

            {/* Amenities */}
            {property.amenities?.length > 0 && (
              <div className="mb-10">
                <h2 className="font-semibold text-navy text-lg mb-5">Amenities</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {property.amenities.map(a => (
                    <div key={a} className="flex items-center gap-3 p-3 bg-[#FAFAF9] rounded-xl border border-gray-50">
                      <div className="w-8 h-8 bg-gold/10 rounded-lg flex items-center justify-center text-gold shrink-0">
                        {AMENITY_MAP[a]?.icon || <CheckCircle size={14} />}
                      </div>
                      <span className="text-sm text-navy capitalize font-medium">{AMENITY_MAP[a]?.label || a}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {property.rules && (
              <div className="mb-10">
                <h2 className="font-semibold text-navy text-lg mb-3">House Rules</h2>
                <p className="text-gray-500 text-sm leading-relaxed">{property.rules}</p>
              </div>
            )}

            {/* Reviews section */}
            <ReviewsSection propertyId={id} propertyName={property.name} />
          </div>

          {/* Right: Book Now (primary) + Send Enquiry (secondary) */}
          <div>
            {/* Panel toggle */}
            <div className="flex gap-2 mb-4">
              <button
                data-testid="book-now-tab"
                onClick={() => setRightPanel('book')}
                className={`flex-1 py-3 rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2 ${
                  rightPanel === 'book'
                    ? 'bg-navy text-white shadow-sm'
                    : 'bg-[#FAFAF9] border border-gray-200 text-gray-500 hover:border-gold hover:text-navy'
                }`}
              >
                Book Now
              </button>
              <button
                data-testid="enquiry-tab"
                onClick={() => setRightPanel('enquiry')}
                className={`flex-1 py-3 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  rightPanel === 'enquiry'
                    ? 'bg-navy text-white shadow-sm'
                    : 'bg-[#FAFAF9] border border-gray-200 text-gray-500 hover:border-gold hover:text-navy'
                }`}
              >
                Send Enquiry
              </button>
            </div>

            {/* Book Now — BookingWidget */}
            {rightPanel === 'book' && (
              <div className="sticky top-24">
                <BookingWidget property={property} />
              </div>
            )}

            {/* Send Enquiry form */}
            {rightPanel === 'enquiry' && (
              <div data-testid="enquiry-card" className="bg-white border border-gray-100 rounded-2xl p-6 sticky top-24"
                style={{ boxShadow: '0 4px 24px rgba(10,22,40,0.08)' }}>
                <h3 className="font-serif text-xl font-bold text-navy mb-1">Send Enquiry</h3>
                <p className="text-gray-400 text-xs mb-6">We'll connect you with the property owner</p>
                {submitted ? (
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
                    <CheckCircle size={40} className="text-green-500 mx-auto mb-3" />
                    <h4 className="font-semibold text-navy mb-2">Enquiry Sent!</h4>
                    <p className="text-sm text-gray-400">The property owner will contact you within 24 hours.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleEnquiry} className="space-y-4">
                    <input required data-testid="enquiry-name" type="text" placeholder="Your name *"
                      value={enquiryForm.name} onChange={e => setEnquiryForm(p => ({ ...p, name: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold" />
                    <input required data-testid="enquiry-email" type="email" placeholder="Email address *"
                      value={enquiryForm.email} onChange={e => setEnquiryForm(p => ({ ...p, email: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold" />
                    <input required data-testid="enquiry-phone" type="tel" placeholder="Phone number *"
                      value={enquiryForm.phone} onChange={e => setEnquiryForm(p => ({ ...p, phone: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold" />
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">Check-in</label>
                        <input type="date" data-testid="enquiry-checkin" value={enquiryForm.check_in}
                          onChange={e => setEnquiryForm(p => ({ ...p, check_in: e.target.value }))}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-navy outline-none focus:border-gold" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">Check-out</label>
                        <input type="date" data-testid="enquiry-checkout" value={enquiryForm.check_out}
                          onChange={e => setEnquiryForm(p => ({ ...p, check_out: e.target.value }))}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-navy outline-none focus:border-gold" />
                      </div>
                    </div>
                    <select data-testid="enquiry-guests" value={enquiryForm.guests}
                      onChange={e => setEnquiryForm(p => ({ ...p, guests: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold">
                      {[1,2,5,10,20,50,100,200,500].map(n => <option key={n} value={n}>{n} guest{n > 1 ? 's' : ''}</option>)}
                    </select>
                    <textarea data-testid="enquiry-message" placeholder="Any special requirements..."
                      value={enquiryForm.message} onChange={e => setEnquiryForm(p => ({ ...p, message: e.target.value }))}
                      rows={3} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy outline-none focus:border-gold resize-none" />
                    {error && <p className="text-red-500 text-xs">{error}</p>}
                    <button data-testid="submit-enquiry" type="submit" disabled={submitting}
                      className="w-full bg-navy hover:bg-navy-light text-white py-3.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                      {submitting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending...</> : <><Phone size={15} />Send Enquiry</>}
                    </button>
                  </form>
                )}
                <p className="text-center text-xs text-gray-400 mt-4">Free enquiry · No booking fees</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PropertyDetail;
